import { quat, type Quat } from '../math/quat'
import { vec3, type Vec3 } from '../math/vec3'
import { pose as poseOps, type Pose } from '../math/pose'
import { DEG2RAD } from '../math/index'
import { unprojectAtDepth, type CameraModel } from '../camera/model'
import type { HandFrame, Handedness } from '../tracking/types'
import { HAND_LM } from '../tracking/landmarks'

/**
 * Metric hand reconstruction. MediaPipe hands provide no transformation matrix, so accuracy
 * comes from fusing two streams:
 *  - normalized landmarks: exact 2-D screen positions (each lifted point stays on its own ray),
 *  - worldLandmarks: metric hand geometry (cm, hand-centred, camera-aligned axes).
 * Depth is a robust weak-perspective fit: for rigid landmark pairs, metric length / angular
 * length = depth; the median over well-spread pairs rejects per-landmark jitter. Per-landmark
 * depth offsets come from world z, so the hand is not flattened to a single plane.
 */
export interface HandMetric {
  /** 21 camera-space points, packed xyz (cm). */
  points: Float32Array
  /** Fitted depth of the hand centroid (cm, positive). */
  depth: number
  /** GEOMETRIC handedness (derived from the thumb side, not MediaPipe's label). */
  handedness: Handedness
  /**
   * Hand frame at the wrist: +Y = wrist→middle-MCP (finger direction),
   * +Z = back of the hand (dorsal normal), +X completes right-handed.
   */
  frame: Pose
  /** Index→pinky MCP span (cm) — real-hand scale reference (bangle/ring sizing). */
  knuckleSpanCm: number
}

/** Landmark pairs used for the depth fit: long, rigid spans across the palm. */
const DEPTH_PAIRS: readonly [number, number][] = [
  [HAND_LM.WRIST, HAND_LM.INDEX_MCP],
  [HAND_LM.WRIST, HAND_LM.MIDDLE_MCP],
  [HAND_LM.WRIST, HAND_LM.RING_MCP],
  [HAND_LM.WRIST, HAND_LM.PINKY_MCP],
  [HAND_LM.INDEX_MCP, HAND_LM.PINKY_MCP],
  [HAND_LM.INDEX_MCP, HAND_LM.RING_MCP],
  [HAND_LM.MIDDLE_MCP, HAND_LM.PINKY_MCP],
  [HAND_LM.WRIST, HAND_LM.THUMB_MCP],
]

const _a: Vec3 = [0, 0, 0]
const _b: Vec3 = [0, 0, 0]
const _y: Vec3 = [0, 0, 0]
const _x: Vec3 = [0, 0, 0]
const _z: Vec3 = [0, 0, 0]
const _ta: Vec3 = [0, 0, 0]
const _tb: Vec3 = [0, 0, 0]

/**
 * @param zSign sign convention of worldLandmarks z relative to camera depth
 *              (MediaPipe: +z = away from camera in image convention → default +1).
 */
export function liftHand(hand: HandFrame, cam: CameraModel, out?: HandMetric, zSign = 1): HandMetric | null {
  const lm = hand.landmarks
  const world = hand.worldLandmarks
  if (!world || lm.length < HAND_LM.COUNT * 3) return null
  const res: HandMetric =
    out ?? {
      points: new Float32Array(HAND_LM.COUNT * 3),
      depth: 0,
      handedness: hand.handedness,
      frame: poseOps.create(),
      knuckleSpanCm: 0,
    }
  // provisional; replaced below by the geometric handedness derived from the thumb side
  res.handedness = hand.handedness

  // ---- robust weak-perspective depth ----------------------------------------
  const t = Math.tan((cam.fovYDeg * DEG2RAD) / 2)
  const depths: number[] = []
  for (const [i, j] of DEPTH_PAIRS) {
    // metric length (world is metres → cm)
    const wx = (world[i * 3]! - world[j * 3]!) * 100
    const wy = (world[i * 3 + 1]! - world[j * 3 + 1]!) * 100
    const wz = (world[i * 3 + 2]! - world[j * 3 + 2]!) * 100
    const metric = Math.hypot(wx, wy, wz)
    // angular length: distance between the two rays at unit depth
    unprojectAtDepth(_a, cam, lm[i * 3]!, lm[i * 3 + 1]!, 1)
    unprojectAtDepth(_b, cam, lm[j * 3]!, lm[j * 3 + 1]!, 1)
    const angular = Math.hypot(_a[0] - _b[0], _a[1] - _b[1])
    if (angular > 1e-5 && metric > 0.5) depths.push(metric / angular)
  }
  if (depths.length < 4) return null
  depths.sort((p, q) => p - q)
  const depth = depths[depths.length >> 1]!
  if (!(depth > 3 && depth < 2000)) return null
  res.depth = depth

  // ---- lift every landmark onto its own ray at world-informed depth ---------
  // world z is metres in image convention (+z away from camera); camera depth adds it.
  let wzMean = 0
  for (let i = 0; i < HAND_LM.COUNT; i++) wzMean += world[i * 3 + 2]! / HAND_LM.COUNT
  for (let i = 0; i < HAND_LM.COUNT; i++) {
    const dz = (world[i * 3 + 2]! - wzMean) * 100 * zSign
    unprojectAtDepth(_a, cam, lm[i * 3]!, lm[i * 3 + 1]!, depth + dz)
    res.points[i * 3] = _a[0]
    res.points[i * 3 + 1] = _a[1]
    res.points[i * 3 + 2] = _a[2]
  }

  // ---- hand frame ------------------------------------------------------------
  const P = (i: number): Vec3 => [res.points[i * 3]!, res.points[i * 3 + 1]!, res.points[i * 3 + 2]!]
  const wrist = P(HAND_LM.WRIST)
  const mMcp = P(HAND_LM.MIDDLE_MCP)
  const iMcp = P(HAND_LM.INDEX_MCP)
  const pMcp = P(HAND_LM.PINKY_MCP)
  vec3.normalize(_y, vec3.sub(_y, mMcp, wrist))
  // Dorsal normal decided GEOMETRICALLY, not from the handedness label (whose mirroring
  // semantics are unreliable and once flipped watch rotation on real webcams): the thumb
  // always sits on the PALM side of the palm plane, for either hand. Take a candidate
  // normal and flip it if the thumb lands on its positive side.
  vec3.sub(_ta, iMcp, wrist)
  vec3.sub(_tb, pMcp, wrist)
  vec3.cross(_z, _ta, _tb)
  vec3.normalize(_z, _z)
  vec3.sub(_ta, P(HAND_LM.THUMB_TIP), wrist)
  vec3.sub(_tb, P(HAND_LM.THUMB_IP), wrist)
  vec3.add(_ta, _ta, _tb)
  if (vec3.dot(_ta, _z) > 0) vec3.negate(_z, _z) // thumb was on +side → that side is the palm
  // orthogonalize: keep dorsal exact, re-derive fingers axis component
  vec3.scaleAndAdd(_y, _y, _z, -vec3.dot(_y, _z))
  vec3.normalize(_y, _y)
  vec3.cross(_x, _y, _z)
  vec3.normalize(_x, _x)
  // geometric handedness: for a right hand, fingers × (index→pinky) points dorsal
  vec3.sub(_ta, iMcp, pMcp)
  vec3.cross(_tb, _y, _ta)
  res.handedness = vec3.dot(_tb, _z) > 0 ? 'right' : 'left'
  // orthonormal basis → quaternion (columns x, y, z)
  const m = new Float32Array(16)
  m[0] = _x[0]; m[1] = _x[1]; m[2] = _x[2]
  m[4] = _y[0]; m[5] = _y[1]; m[6] = _y[2]
  m[8] = _z[0]; m[9] = _z[1]; m[10] = _z[2]
  m[15] = 1
  quat.fromMat4(res.frame.rotation, m)
  vec3.copy(res.frame.position, wrist)
  res.frame.scale = 1
  res.knuckleSpanCm = vec3.distance(iMcp, pMcp)
  return res
}

export type FingerName = 'thumb' | 'index' | 'middle' | 'ring' | 'pinky'

export const FINGER_CHAIN: Record<FingerName, [number, number, number, number]> = {
  thumb: [HAND_LM.THUMB_CMC, HAND_LM.THUMB_MCP, HAND_LM.THUMB_IP, HAND_LM.THUMB_TIP],
  index: [HAND_LM.INDEX_MCP, HAND_LM.INDEX_PIP, HAND_LM.INDEX_DIP, HAND_LM.INDEX_TIP],
  middle: [HAND_LM.MIDDLE_MCP, HAND_LM.MIDDLE_PIP, HAND_LM.MIDDLE_DIP, HAND_LM.MIDDLE_TIP],
  ring: [HAND_LM.RING_MCP, HAND_LM.RING_PIP, HAND_LM.RING_DIP, HAND_LM.RING_TIP],
  pinky: [HAND_LM.PINKY_MCP, HAND_LM.PINKY_PIP, HAND_LM.PINKY_DIP, HAND_LM.PINKY_TIP],
}

export interface RingSolveResult {
  /** Ring pose: origin on the finger axis, +Z along the finger (MCP→PIP), +Y dorsal. */
  pose: Pose
  /** Estimated finger diameter (cm) from neighbouring MCP spacing. */
  fingerDiameterCm: number
}

const _dir: Vec3 = [0, 0, 0]
const _mid: Vec3 = [0, 0, 0]
const _up: Vec3 = [0, 0, 0]
const _side: Vec3 = [0, 0, 0]
const _qz: Quat = [0, 0, 0, 1]

/**
 * Ring solver: seat the band on the proximal phalanx at `seat` ∈ [0,1] between MCP and PIP
 * (rings rest near the base → default 0.3). Orientation: +Z along the finger, +Y toward the
 * back of the hand, so a stone on top faces the dorsal side.
 */
export function solveRing(
  hand: HandMetric,
  finger: FingerName,
  spec: { offset: Vec3; rotation: Vec3; scale: number },
  seat = 0.3,
  out?: RingSolveResult,
): RingSolveResult {
  const res: RingSolveResult = out ?? { pose: poseOps.create(), fingerDiameterCm: 1.7 }
  const [mcp, pip] = FINGER_CHAIN[finger]
  const P = (i: number): Vec3 => [hand.points[i * 3]!, hand.points[i * 3 + 1]!, hand.points[i * 3 + 2]!]
  const a = P(mcp)
  const b = P(pip!)
  vec3.lerp(_mid, a, b, seat)
  vec3.normalize(_dir, vec3.sub(_dir, b, a))
  // dorsal direction from the hand frame's +Z, orthogonalized against the finger axis
  quat.rotateVec3(_up, hand.frame.rotation, [0, 0, 1])
  vec3.scaleAndAdd(_up, _up, _dir, -vec3.dot(_up, _dir))
  vec3.normalize(_up, _up)
  vec3.cross(_side, _up, _dir)
  const m = new Float32Array(16)
  m[0] = _side[0]; m[1] = _side[1]; m[2] = _side[2]
  m[4] = _up[0]; m[5] = _up[1]; m[6] = _up[2]
  m[8] = _dir[0]; m[9] = _dir[1]; m[10] = _dir[2]
  m[15] = 1
  quat.fromMat4(res.pose.rotation, m)
  quat.fromEulerDeg(_qz, spec.rotation[0], spec.rotation[1], spec.rotation[2])
  quat.multiply(res.pose.rotation, res.pose.rotation, _qz)
  quat.rotateVec3(_up, res.pose.rotation, spec.offset)
  vec3.add(res.pose.position, _mid, _up)
  res.pose.scale = spec.scale
  // finger diameter ≈ 0.55 × the gap between this MCP and its nearest neighbour
  const neighbour: Record<FingerName, number> = {
    thumb: HAND_LM.INDEX_MCP, index: HAND_LM.MIDDLE_MCP, middle: HAND_LM.INDEX_MCP,
    ring: HAND_LM.MIDDLE_MCP, pinky: HAND_LM.RING_MCP,
  }
  res.fingerDiameterCm = Math.max(1.2, Math.min(2.4, vec3.distance(P(mcp), P(neighbour[finger])) * 0.55 * 2))
  return res
}

/**
 * Move a lifted hand to a (smoothed) depth: every point sits on a camera ray through the origin,
 * so a uniform rescale keeps 2-D alignment exact while eliminating frame-to-frame depth jitter.
 * Directions (and therefore the hand frame's rotation) are unchanged.
 */
export function rescaleHandDepth(m: HandMetric, newDepth: number): void {
  if (!(m.depth > 1e-3) || !(newDepth > 0)) return
  const k = newDepth / m.depth
  for (let i = 0; i < m.points.length; i++) m.points[i] = m.points[i]! * k
  m.frame.position[0] *= k
  m.frame.position[1] *= k
  m.frame.position[2] *= k
  m.knuckleSpanCm *= k
  m.depth = newDepth
}
